import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  useSearchParams,
  type SetURLSearchParams,
  type URLSearchParamsInit,
} from 'react-router-dom'

/** Isolated catalog query state for the order-page floating panel (no nested Router). */
type EmbeddedCtx = {
  searchParams: URLSearchParams
  setSearchParams: SetURLSearchParams
}

const EmbeddedCatalogParamsContext = createContext<EmbeddedCtx | null>(null)

/** DOM `URLSearchParams` ctor types are narrower than react-router's `URLSearchParamsInit`. */
function searchParamsFromInit(
  init: URLSearchParamsInit | undefined
): URLSearchParams {
  if (init === undefined) {
    return new URLSearchParams()
  }
  if (init instanceof URLSearchParams) {
    return new URLSearchParams(init)
  }
  if (typeof init === 'string') {
    return new URLSearchParams(init)
  }
  if (Array.isArray(init)) {
    return new URLSearchParams(init)
  }
  const pairs: [string, string][] = []
  for (const [key, value] of Object.entries(init)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        pairs.push([key, item])
      }
    } else {
      pairs.push([key, value])
    }
  }
  return new URLSearchParams(pairs)
}

export const EmbeddedCatalogParamsProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [searchParams, setSearchParamsState] = useState(
    () => new URLSearchParams()
  )

  const setSearchParams = useCallback<SetURLSearchParams>((nextInit, _) => {
    setSearchParamsState((prev) => {
      const prevParams = new URLSearchParams(prev)
      if (typeof nextInit === 'function') {
        return searchParamsFromInit(nextInit(prevParams))
      }
      return searchParamsFromInit(nextInit)
    })
  }, [])

  const value = useMemo(
    () => ({ searchParams, setSearchParams }),
    [searchParams, setSearchParams]
  )

  return (
    <EmbeddedCatalogParamsContext.Provider value={value}>
      {children}
    </EmbeddedCatalogParamsContext.Provider>
  )
}

export const useEmbeddedCatalogParamsOptional = () =>
  useContext(EmbeddedCatalogParamsContext)

/**
 * In the floating order panel, reads/writes isolated params via context.
 * On `/all-products`, uses the real location search params.
 */
export function useProductsCatalogSearchParams(): [
  URLSearchParams,
  SetURLSearchParams,
] {
  const embedded = useEmbeddedCatalogParamsOptional()
  const [routerParams, setRouterParams] = useSearchParams()

  if (embedded) {
    return [embedded.searchParams, embedded.setSearchParams]
  }
  return [routerParams, setRouterParams]
}
